import React, { useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw, FileText, Eye, Download, AlertTriangle, FolderArchive, Package, X, Trash2, Users, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { useDocumentViewer } from '../components/DocumentViewer';

const API = process.env.REACT_APP_BACKEND_URL;

const SOURCE_META = {
  recruiter: { label: 'Recruiter', color: 'bg-amber-100 text-amber-700' },
  student: { label: 'Student', color: 'bg-violet-100 text-violet-700' },
  agreement: { label: 'Agreement', color: 'bg-emerald-100 text-emerald-700' },
  chat: { label: 'Chat', color: 'bg-sky-100 text-sky-700' },
};

const fmtBytes = (n) => {
  if (n === null || n === undefined) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-CA', { timeZone: 'America/Vancouver', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return iso;
  }
};

export default function DocumentsArchive() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all | recruiter | student | agreement | chat
  const [query, setQuery] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [owners, setOwners] = useState([]);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [ownerTypeFilter, setOwnerTypeFilter] = useState('all'); // all | recruiter | student
  const [zipping, setZipping] = useState(null); // `${type}:${id}` currently zipping
  const [selected, setSelected] = useState(() => new Set()); // doc.id values
  const [deleting, setDeleting] = useState(false);
  const [me, setMe] = useState(null);
  const [groupByStudent, setGroupByStudent] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const viewer = useDocumentViewer();

  // Admin or manager can delete from the archive. Counsellors cannot — even
  // with documents.edit. (Decision i: financial-grade archive, top-tier only.)
  const canDelete = me?.role === 'admin' || me?.role === 'manager';

  const fetchDocs = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/documents`, { credentials: 'include' });
      if (!res.ok) {
        if (res.status === 404) {
          setError('Document archive endpoint not available. Please redeploy production to apply latest backend changes.');
          setDocs([]);
          return;
        }
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || `Failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      setDocs(data.documents || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
    // Cheap me-fetch so we can gate the delete UI without prop-drilling.
    fetch(`${API}/api/auth/me`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then(setMe)
      .catch(() => {});
  }, []);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = (rows) => {
    setSelected((prev) => {
      const allSelected = rows.length > 0 && rows.every((r) => prev.has(r.id));
      return allSelected ? new Set() : new Set(rows.map((r) => r.id));
    });
  };

  const deleteOne = async (doc) => {
    if (!window.confirm(`Delete "${doc.label}" for ${doc.owner_name}? This removes the file from cloud storage AND clears the reference. This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/admin/documents/${encodeURIComponent(doc.id)}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      setSelected((s) => { const n = new Set(s); n.delete(doc.id); return n; });
      await fetchDocs();
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const deleteBulk = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} document${ids.length > 1 ? 's' : ''}? This removes the files from cloud storage AND clears the references. This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/admin/documents/bulk-delete`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || `HTTP ${res.status}`);
      setSelected(new Set());
      await fetchDocs();
      const failed = body.failed?.length || 0;
      const ok = body.deleted || 0;
      alert(`Deleted ${ok} document${ok !== 1 ? 's' : ''}.${failed ? ` ${failed} failed.` : ''}`);
    } catch (e) {
      alert(`Bulk delete failed: ${e.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const fetchOwners = async () => {
    try {
      const res = await fetch(`${API}/api/admin/documents/owners`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setOwners(data.owners || []);
    } catch (_) {}
  };

  const openBulk = async () => {
    setBulkOpen(true);
    setOwnerSearch('');
    setOwnerTypeFilter('all');
    fetchOwners();
  };

  const downloadOwnerZip = async (owner) => {
    const key = `${owner.type}:${owner.id}`;
    setZipping(key);
    try {
      const url = `${API}/api/admin/documents/zip?owner_type=${owner.type}&owner_id=${encodeURIComponent(owner.id)}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        alert(d.detail || `Failed (HTTP ${res.status})`);
        return;
      }
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `${owner.name.replace(/[^\w-]+/g, '-')}-documents.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
    } catch (e) {
      alert(e.message);
    } finally {
      setZipping(null);
    }
  };

  const filteredOwners = useMemo(() => {
    const q = ownerSearch.trim().toLowerCase();
    return owners.filter((o) => {
      if (ownerTypeFilter !== 'all' && o.type !== ownerTypeFilter) return false;
      if (!q) return true;
      return (o.name || '').toLowerCase().includes(q) || (o.id || '').toLowerCase().includes(q);
    });
  }, [owners, ownerSearch, ownerTypeFilter]);

  const counts = useMemo(() => {
    const c = { all: docs.length, recruiter: 0, student: 0, agreement: 0, chat: 0 };
    for (const d of docs) c[d.source] = (c[d.source] || 0) + 1;
    return c;
  }, [docs]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return docs.filter((d) => {
      if (filter !== 'all' && d.source !== filter) return false;
      if (!q) return true;
      return (
        (d.owner_name || '').toLowerCase().includes(q) ||
        (d.label || '').toLowerCase().includes(q) ||
        (d.filename || '').toLowerCase().includes(q) ||
        (d.doc_key || '').toLowerCase().includes(q)
      );
    });
  }, [docs, filter, query]);

  // Student-grouped view. We only group docs whose source is 'student' (the
  // owner_id then maps to a student profile). Other sources collapse into a
  // single "Other documents" bucket so nothing is hidden when the toggle is on.
  const studentGroups = useMemo(() => {
    const groups = new Map();
    const other = [];
    for (const d of filtered) {
      if (d.source === 'student' && d.owner_id) {
        const key = d.owner_id;
        if (!groups.has(key)) {
          groups.set(key, {
            owner_id: d.owner_id,
            owner_name: d.owner_name || 'Unknown student',
            owner_role: d.owner_role || 'student',
            docs: [],
          });
        }
        groups.get(key).docs.push(d);
      } else {
        other.push(d);
      }
    }
    const list = Array.from(groups.values()).sort((a, b) =>
      (a.owner_name || '').localeCompare(b.owner_name || '')
    );
    return { groups: list, other };
  }, [filtered]);

  const toggleGroup = (id) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const downloadDirect = async (doc) => {
    try {
      const url = doc.download_url.startsWith('http') ? doc.download_url : `${API}${doc.download_url}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        alert("Couldn't download — the file may be missing or you lack permission.");
        return;
      }
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = doc.filename || `${doc.owner_name || 'file'}-${doc.doc_key}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <div className="space-y-5" data-testid="documents-archive">
      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        {[
          { k: 'all', label: 'All', count: counts.all },
          { k: 'recruiter', label: 'Recruiter docs', count: counts.recruiter },
          { k: 'student', label: 'Student docs', count: counts.student },
          { k: 'agreement', label: 'Signed agreements', count: counts.agreement },
          { k: 'chat', label: 'Chat attachments', count: counts.chat },
        ].map((opt) => (
          <button
            key={opt.k}
            onClick={() => setFilter(opt.k)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${
              filter === opt.k
                ? 'bg-tge-navy text-white border-tge-navy'
                : 'border-tge-navy/15 text-tge-navy/70 hover:bg-tge-cream-soft hover:text-tge-navy'
            }`}
            data-testid={`docs-filter-${opt.k}`}
          >
            {opt.label} <span className="opacity-70">· {opt.count}</span>
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={() => setGroupByStudent((v) => !v)}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold transition px-3.5 py-1.5 rounded-full border ${
            groupByStudent
              ? 'bg-violet-600 text-white border-violet-600 hover:bg-violet-700'
              : 'border-tge-navy/15 text-tge-navy/70 hover:bg-tge-cream-soft hover:text-tge-navy'
          }`}
          data-testid="docs-group-by-student-toggle"
          title="Group documents by student"
        >
          <Users size={12} /> {groupByStudent ? 'Grouped by student' : 'Group by student'}
        </button>
        <button
          onClick={openBulk}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-tge-navy hover:bg-tge-navy-deep transition px-3.5 py-1.5 rounded-full"
          data-testid="docs-bulk-download-btn"
        >
          <Package size={12} /> Download as ZIP
        </button>
        <button
          onClick={fetchDocs}
          className="inline-flex items-center gap-1.5 text-xs text-tge-muted hover:text-tge-navy transition px-3 py-1.5 rounded-full border border-tge-navy/10 hover:border-tge-navy/30 hover:bg-tge-cream-soft"
          data-testid="docs-refresh"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-tge-navy/40" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by owner, document type, or filename..."
          className="pl-9"
          data-testid="docs-search"
        />
      </div>

      {/* Body */}
      {error ? (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 flex items-start gap-3">
          <AlertTriangle className="text-rose-600 shrink-0" size={18} />
          <div>
            <div className="font-semibold text-rose-800 text-sm">{error}</div>
          </div>
        </div>
      ) : loading ? (
        <div className="text-center py-16 text-tge-muted text-sm">Loading documents...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-tge-navy/5 text-center py-20">
          <FolderArchive size={36} className="mx-auto text-tge-navy/20 mb-3" />
          <div className="text-tge-navy/60 text-sm">
            {query || filter !== 'all'
              ? 'No documents match your filter.'
              : 'No documents have been uploaded yet.'}
          </div>
        </div>
      ) : groupByStudent ? (
        <div className="space-y-4" data-testid="docs-grouped-view">
          {studentGroups.groups.length === 0 && studentGroups.other.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              No student-owned documents in the current filter. Switch filter to &quot;All&quot; or &quot;Student docs&quot; to see grouped students.
            </div>
          )}
          {studentGroups.groups.map((g) => {
            const collapsed = collapsedGroups.has(g.owner_id);
            const zipKey = `student:${g.owner_id}`;
            const busy = zipping === zipKey;
            return (
              <div
                key={g.owner_id}
                className="bg-white rounded-2xl border border-tge-navy/10 overflow-hidden shadow-sm"
                data-testid={`docs-group-${g.owner_id}`}
              >
                <div className="px-5 py-3.5 bg-gradient-to-r from-violet-50 to-tge-cream-soft border-b border-tge-navy/5 flex items-center gap-3">
                  <button
                    onClick={() => toggleGroup(g.owner_id)}
                    className="w-7 h-7 rounded-lg hover:bg-white/60 flex items-center justify-center text-tge-navy/60 transition shrink-0"
                    aria-label={collapsed ? 'Expand group' : 'Collapse group'}
                    data-testid={`docs-group-toggle-${g.owner_id}`}
                  >
                    {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                  </button>
                  <div className="w-10 h-10 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-semibold shrink-0">
                    {(g.owner_name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-tge-navy truncate">{g.owner_name}</div>
                    <div className="text-[11px] text-tge-navy/50 font-mono">
                      {g.owner_id} · {g.docs.length} {g.docs.length === 1 ? 'file' : 'files'}
                    </div>
                  </div>
                  <button
                    onClick={() => downloadOwnerZip({ type: 'student', id: g.owner_id, name: g.owner_name })}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-tge-navy hover:bg-tge-navy-deep transition px-3.5 py-1.5 rounded-full disabled:opacity-60 shrink-0"
                    data-testid={`docs-group-zip-${g.owner_id}`}
                  >
                    {busy ? (
                      <>
                        <span className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-white" /> Zipping...
                      </>
                    ) : (
                      <>
                        <Package size={12} /> Download all (ZIP)
                      </>
                    )}
                  </button>
                </div>
                {!collapsed && (
                  <div className="divide-y divide-tge-cream">
                    {g.docs.map((d) => (
                      <div
                        key={d.id}
                        className={`px-5 py-3 flex items-center gap-3 hover:bg-tge-cream-soft/40 transition ${d.missing ? 'opacity-60' : ''}`}
                        data-testid={`doc-row-${d.id}`}
                      >
                        {canDelete && (
                          <input
                            type="checkbox"
                            checked={selected.has(d.id)}
                            onChange={() => toggleOne(d.id)}
                            className="accent-tge-navy w-4 h-4 cursor-pointer"
                            aria-label={`Select ${d.label}`}
                            data-testid={`doc-select-${d.id}`}
                          />
                        )}
                        <div className="w-9 h-9 rounded-xl bg-tge-navy/5 text-tge-navy/60 flex items-center justify-center shrink-0">
                          <FileText size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-tge-navy truncate capitalize">{d.label}</div>
                          <div className="text-[11px] text-tge-navy/50 truncate font-mono">{d.filename}</div>
                          {d.missing && <div className="text-[10px] text-rose-600 mt-0.5">⚠ File missing on disk</div>}
                        </div>
                        <div className="text-[11px] text-tge-navy/50 shrink-0 hidden md:block">{fmtBytes(d.size)}</div>
                        <div className="text-[11px] text-tge-navy/50 shrink-0 hidden lg:block">{fmtDate(d.uploaded_at)}</div>
                        <div className="flex gap-1.5 shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs gap-1 h-8"
                            disabled={d.missing}
                            onClick={() => viewer.open({
                              url: d.download_url,
                              label: d.label,
                              ownerName: d.owner_name,
                            })}
                            data-testid={`doc-view-${d.id}`}
                          >
                            <Eye size={12} /> View
                          </Button>
                          <Button
                            size="sm"
                            className="text-xs bg-tge-navy hover:bg-tge-navy-deep text-white gap-1 h-8"
                            disabled={d.missing}
                            onClick={() => downloadDirect(d)}
                            data-testid={`doc-download-${d.id}`}
                          >
                            <Download size={12} /> Download
                          </Button>
                          {canDelete && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs gap-1 h-8 text-rose-600 hover:bg-rose-50 hover:text-rose-700 border-rose-200"
                              onClick={() => deleteOne(d)}
                              disabled={deleting}
                              title="Delete document (admin/manager only)"
                              data-testid={`doc-delete-${d.id}`}
                            >
                              <Trash2 size={12} />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {studentGroups.other.length > 0 && (
            <div className="bg-white rounded-2xl border border-tge-navy/10 overflow-hidden shadow-sm" data-testid="docs-group-other">
              <div className="px-5 py-3.5 bg-tge-cream-soft border-b border-tge-navy/5">
                <div className="text-sm font-semibold text-tge-navy">Other documents</div>
                <div className="text-[11px] text-tge-navy/50">Recruiter, agreement, and chat attachments ({studentGroups.other.length})</div>
              </div>
              <div className="divide-y divide-tge-cream">
                {studentGroups.other.map((d) => (
                  <div key={d.id} className={`px-5 py-3 flex items-center gap-3 hover:bg-tge-cream-soft/40 transition ${d.missing ? 'opacity-60' : ''}`}>
                    <div className="w-9 h-9 rounded-xl bg-tge-navy/5 text-tge-navy/60 flex items-center justify-center shrink-0">
                      <FileText size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-tge-navy truncate capitalize">{d.label}</div>
                      <div className="text-[11px] text-tge-navy/50 truncate">{d.owner_name} · <span className="capitalize">{d.source}</span></div>
                    </div>
                    <Button
                      size="sm"
                      className="text-xs bg-tge-navy hover:bg-tge-navy-deep text-white gap-1 h-8"
                      disabled={d.missing}
                      onClick={() => downloadDirect(d)}
                    >
                      <Download size={12} /> Download
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-tge-navy/5 overflow-hidden">
          {canDelete && selected.size > 0 && (
            <div className="px-4 py-2.5 bg-rose-50 border-b border-rose-200 flex items-center justify-between" data-testid="docs-bulk-bar">
              <div className="text-sm text-rose-800">
                <span className="font-semibold">{selected.size}</span> document{selected.size !== 1 ? 's' : ''} selected
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm" variant="outline" className="h-8 text-xs"
                  onClick={() => setSelected(new Set())}
                  data-testid="docs-bulk-clear"
                >
                  Clear
                </Button>
                <Button
                  size="sm" className="h-8 text-xs bg-rose-600 hover:bg-rose-700 text-white gap-1"
                  onClick={deleteBulk}
                  disabled={deleting}
                  data-testid="docs-bulk-delete"
                >
                  <Trash2 size={12} /> Delete selected
                </Button>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-tge-cream-soft">
                <tr>
                  {canDelete && (
                    <th className="px-3 py-3 w-8">
                      <input
                        type="checkbox"
                        checked={filtered.length > 0 && filtered.every((d) => selected.has(d.id))}
                        onChange={() => toggleAll(filtered)}
                        className="accent-tge-navy w-4 h-4 cursor-pointer"
                        aria-label="Select all visible"
                        data-testid="docs-select-all"
                      />
                    </th>
                  )}
                  {['Document', 'Owner', 'Source', 'Size', 'Uploaded', 'Actions'].map((h) => (
                    <th key={h} className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-tge-muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-tge-cream">
                {filtered.map((d) => (
                  <tr key={d.id} className={`hover:bg-tge-cream-soft/40 transition ${d.missing ? 'opacity-60' : ''} ${selected.has(d.id) ? 'bg-rose-50/40' : ''}`} data-testid={`doc-row-${d.id}`}>
                    {canDelete && (
                      <td className="px-3 py-4 w-8">
                        <input
                          type="checkbox"
                          checked={selected.has(d.id)}
                          onChange={() => toggleOne(d.id)}
                          className="accent-tge-navy w-4 h-4 cursor-pointer"
                          aria-label={`Select ${d.label}`}
                          data-testid={`doc-select-${d.id}`}
                        />
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-tge-navy/5 text-tge-navy/60 flex items-center justify-center shrink-0">
                          <FileText size={16} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-tge-navy truncate capitalize">{d.label}</div>
                          <div className="text-[11px] text-tge-navy/50 truncate font-mono">{d.filename}</div>
                          {d.missing && <div className="text-[10px] text-rose-600 mt-0.5">⚠ File missing on disk</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-tge-navy">
                      <div className="font-medium truncate max-w-[200px]">{d.owner_name}</div>
                      <div className="text-[11px] text-tge-navy/50 capitalize">{d.owner_role || d.source}</div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={`${SOURCE_META[d.source]?.color || 'bg-gray-100 text-gray-700'} border-0`}>
                        {SOURCE_META[d.source]?.label || d.source}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-xs text-tge-navy/60">{fmtBytes(d.size)}</td>
                    <td className="px-6 py-4 text-xs text-tge-navy/60">{fmtDate(d.uploaded_at)}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs gap-1 h-8"
                          disabled={d.missing}
                          onClick={() => viewer.open({
                            url: d.download_url,
                            label: d.label,
                            ownerName: d.owner_name,
                          })}
                          data-testid={`doc-view-${d.id}`}
                        >
                          <Eye size={12} /> View
                        </Button>
                        <Button
                          size="sm"
                          className="text-xs bg-tge-navy hover:bg-tge-navy-deep text-white gap-1 h-8"
                          disabled={d.missing}
                          onClick={() => downloadDirect(d)}
                          data-testid={`doc-download-${d.id}`}
                        >
                          <Download size={12} /> Download
                        </Button>
                        {canDelete && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs gap-1 h-8 text-rose-600 hover:bg-rose-50 hover:text-rose-700 border-rose-200"
                            onClick={() => deleteOne(d)}
                            disabled={deleting}
                            title="Delete document (admin/manager only)"
                            data-testid={`doc-delete-${d.id}`}
                          >
                            <Trash2 size={12} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bulk download dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg w-[92vw] p-0 gap-0 overflow-hidden flex flex-col max-h-[80vh]" data-testid="bulk-download-dialog">
          <div className="px-5 py-4 border-b border-tge-navy/10 flex items-center justify-between bg-tge-cream-soft">
            <div className="flex items-center gap-2">
              <Package size={16} className="text-tge-navy" />
              <div className="font-serif-display text-tge-navy text-base">Bulk download as ZIP</div>
            </div>
            <button
              onClick={() => setBulkOpen(false)}
              className="w-8 h-8 rounded-lg hover:bg-tge-navy/10 flex items-center justify-center text-tge-navy/60 transition"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="px-5 py-3 border-b border-tge-navy/5 space-y-2.5 shrink-0">
            <div className="flex items-center gap-2">
              {[
                { k: 'all', label: 'All' },
                { k: 'recruiter', label: 'Recruiters' },
                { k: 'student', label: 'Students' },
              ].map((opt) => (
                <button
                  key={opt.k}
                  onClick={() => setOwnerTypeFilter(opt.k)}
                  className={`text-[11px] px-3 py-1 rounded-full border transition ${
                    ownerTypeFilter === opt.k
                      ? 'bg-tge-navy text-white border-tge-navy'
                      : 'border-tge-navy/15 text-tge-navy/70 hover:bg-tge-cream-soft'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-tge-navy/40" />
              <Input
                value={ownerSearch}
                onChange={(e) => setOwnerSearch(e.target.value)}
                placeholder="Search recruiter or student..."
                className="pl-9 h-9 text-sm"
                data-testid="bulk-download-search"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {filteredOwners.length === 0 ? (
              <div className="text-center py-12 text-tge-navy/50 text-sm">
                {owners.length === 0 ? 'No owners with documents yet.' : 'No results match your filter.'}
              </div>
            ) : (
              filteredOwners.map((o) => {
                const key = `${o.type}:${o.id}`;
                const busy = zipping === key;
                return (
                  <button
                    key={key}
                    onClick={() => downloadOwnerZip(o)}
                    disabled={busy}
                    className="w-full text-left px-5 py-3 border-b border-tge-navy/5 hover:bg-tge-cream-soft/40 flex items-center gap-3 transition disabled:opacity-60"
                    data-testid={`bulk-owner-${key}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold ${
                      o.type === 'recruiter' ? 'bg-amber-100 text-amber-700' : 'bg-violet-100 text-violet-700'
                    }`}>
                      {(o.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-tge-navy truncate">{o.name}</div>
                      <div className="text-[11px] text-tge-navy/50 capitalize">
                        {o.type} · {o.doc_count} {o.doc_count === 1 ? 'file' : 'files'} · {o.id}
                      </div>
                    </div>
                    <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-tge-navy bg-white border border-tge-navy/15 px-2.5 py-1 rounded-full shrink-0">
                      {busy ? (
                        <>
                          <span className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-tge-navy" /> Zipping...
                        </>
                      ) : (
                        <>
                          <Download size={12} /> ZIP
                        </>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

      {viewer.element}
    </div>
  );
}
